import { GuidePriority } from "../guide";
import type { GuideContext, GuideScript, GuideStep, GuideTopicId, HubFacts } from "../guide";
import { featureHubSteps } from "./features";

/**
 * Captain Bram in the hub (DESIGN §6.1 and §6.5): the first session (after the first win: equip → upgrade → PLAY
 * level 2) and the hub hints that follow it. A brand-new player first sees the hub after that win: the title takes
 * them straight into level 1, where the welcome card now lives (scripts/level1.ts l1.welcome; the hub's welcome and
 * its PLAY lesson are retired, guideSave.ts RETIRED_STEP_IDS). It replaces ui/hub/coach.ts and the Armoury's
 * level-gate coachbox. Pure data over HubFacts: hubView.ts builds the facts and hosts the overlay.
 */

/** The first clear: Gear and Chests open with it, and the coached first upgrade follows it (unlockCards HUB_UNLOCK_LEVEL). */
const FIRST_WIN = 1;
/** The hub hints wait until the first session is over: level 2 cleared (a skipped walkthrough never marks hub.play2). */
const HINTS_FROM_CLEARED = 2;

/**
 * The first-session steps, in order (hub.firstWin … hub.play2). While any is neither done nor skipped the guide is not
 * quiet: the check-in card, the install card and order toasts wait (DESIGN §4.2). `skipWalkthrough` suppresses them.
 */
export const HUB_FIRST_SESSION_IDS = [
  "hub.firstWin", "hub.equip", "hub.equipBest", "hub.upgrade", "hub.upgradeGo", "hub.upgradeWatch", "hub.play2",
] as const;

/**
 * Hub selectors the scripts point at. The PLAY slot holds CONTINUE while a battle is suspended (keep.css hides PLAY),
 * so the list's first visible match is whichever is on screen.
 */
export const HubTargets = {
  play: ".hub .btn.play, .hub .controw .cont",
  gearTab: ".tab[data-tab=gear]",
  towersTab: ".tab[data-tab=towers]",
  /** M35: the Events tab (Daily Orders and the events; the Chests tab's Orders moved there). */
  eventsTab: ".tab[data-tab=events]",
  /** M35: the top bar's ⚙ menu (Settings, the Guide, Achievements: the More tab's rows that are not a shop's or an event's). */
  menuButton: ".hub .topbar .menubtn",
  builderOrTowers: ".bchip:not(.hidden), .tab[data-tab=towers]",
  builderChip: ".bchip:not(.hidden)",
  starRow: ".battlecard .starrow",
  /**
   * M30 #15 (Equip best is gone): the item to put on — the "For X" strip's first tile (best gain first; its card has
   * Equip). A selector list resolves in document order and the Armoury redesign (2026-10-21) put the paper doll above
   * the strip, so a socket here won over the item (and a socket opens the picker, not Equip): when the strip lists no
   * items for this owner, the line-up tile of one that can improve (its red count) instead. The step id stays
   * `hub.equipBest` so saves that finished it keep it finished.
   */
  equipBest: ".arm-for.m-items .row > .gtile:first-child, .arm-body:not(:has(.arm-for.m-items)) .arm-owners .otile:has(> .dot:not(.mute))",
  upgradeGo: ".sheet.upgrade .btn.upgrade",
} as const;

/**
 * Targets that live inside a hub sheet: a sheet holding one is not "busy" for the guide (hubGuide.ts), so a step can
 * point into the Armoury or the upgrade sheet. Every other hub step asks for `sheet === null` in its `when`.
 */
export const HUB_SHEET_TARGETS: readonly string[] = [HubTargets.equipBest, HubTargets.upgradeGo];

/** Towers that unlock after level 1 and get a "ready" line in the hub, by id (balance towers; names as they read). */
const NEW_TOWERS: readonly { readonly id: string; readonly name: string; readonly topic?: GuideTopicId }[] = [
  { id: "fire", name: "Fire" }, { id: "lightning", name: "Lightning" }, { id: "ballista", name: "Ballista" },
  { id: "poison", name: "Poison" }, { id: "beacon", name: "Beacon" },
  // TOWERS_V2: their names are already a building ("the Hex Totem is ready", not "the Hex Totem tower")
  { id: "barracks", name: "Barracks", topic: "barracks" }, { id: "arcane", name: "Arcane Spire", topic: "arcane" },
  { id: "mortar", name: "Mortar", topic: "mortar" }, { id: "hex", name: "Hex Totem", topic: "hex" },
];

const hub = (c: GuideContext): HubFacts | null => (c.screen === "hub" ? c.hub : null);
/** On the lobby itself, no sheet up: the step points at the lobby. */
const lobby = (c: GuideContext): HubFacts | null => { const h = hub(c); return h && h.sheet === null ? h : null; };
const firstWinHub = (c: GuideContext): HubFacts | null => { const h = hub(c); return h && h.highestCleared === FIRST_WIN ? h : null; };
/** The first win's goals: something worn (or nothing to wear), and a builder at work (or nothing it can afford). */
const equipDone = (h: HubFacts) => h.equipped || h.stashCount === 0;
const upgradeDone = (h: HubFacts) => h.upgraded || h.builderSuggestion === null;
const pressed = (c: GuideContext) => c.signals.has("playPressed");
const atGate = (h: HubFacts) => h.gearRequired && h.nextLevel === h.gearRequiredFrom;

const firstSession: GuideStep[] = [
  {
    // The player's first sight of the hub (they came from the title straight into level 1): say where they are.
    id: "hub.firstWin", kind: "info", pose: "cheering", screen: "hub", priority: GuidePriority.Walkthrough, topic: "chests",
    // The end screen has just cheered the win (end.firstWin "We did it, Commander!"): this line says where they are.
    text: "Welcome to the **fortress**, the heart of the Highmarch! Here we turn our spoils into strength.",
    when: (c) => lobby(c)?.highestCleared === FIRST_WIN,
  },
  {
    id: "hub.equip", kind: "do", pose: "pointing", screen: "hub", priority: GuidePriority.Walkthrough, after: ["hub.firstWin"], topic: "gear",
    target: { dom: HubTargets.gearTab }, block: "hard",
    text: "Tap the **Armoury**. That chest held something for us.",
    when: (c) => { const h = firstWinHub(c); return h !== null && h.sheet === null && !equipDone(h); },
    until: (c) => { const h = hub(c); return h === null || h.sheet === "gear" || h.equipped; },
  },
  {
    id: "hub.equipBest", kind: "do", pose: "pointing", screen: "hub", priority: GuidePriority.Walkthrough, after: ["hub.firstWin"], topic: "gear",
    // Not a hard block: the tap opens the item card, whose Equip button must stay reachable.
    target: { dom: HubTargets.equipBest }, block: "none",
    text: "Tap the new item, then **Equip**. Gear makes your towers stronger.",
    when: (c) => { const h = firstWinHub(c); return h !== null && h.sheet === "gear" && !h.equipped; },
    until: (c) => c.signals.has("gearEquipped") || hub(c)?.equipped === true || hub(c)?.sheet !== "gear",
  },
  {
    id: "hub.upgrade", kind: "do", pose: "pointing", screen: "hub", priority: GuidePriority.Walkthrough, after: ["hub.firstWin"], topic: "builders",
    target: { dom: HubTargets.builderOrTowers }, block: "hard",
    text: (c) => {
      const s = hub(c)?.builderSuggestion;
      return s ? `Now let's put our builder to work. Upgrade the **${s.name}**.` : "Now let's put our builder to work. Open **Towers** and upgrade one.";
    },
    when: (c) => { const h = firstWinHub(c); return h !== null && h.sheet === null && equipDone(h) && !upgradeDone(h); },
    // The Towers page counts too: from there the player picks the tower, and a hard block over it would trap them.
    until: (c) => { const h = hub(c); return h === null || h.sheet === "upgrade" || h.sheet === "towers" || h.upgraded; },
  },
  {
    id: "hub.upgradeGo", kind: "do", pose: "pointing", screen: "hub", priority: GuidePriority.Walkthrough, after: ["hub.firstWin"], topic: "builders",
    target: { dom: HubTargets.upgradeGo }, block: "hard",
    text: (c) => {
      const s = hub(c)?.builderSuggestion;
      return s ? `Tap **UPGRADE**. Just ${s.seconds} seconds.` : "Tap **UPGRADE**. Our builder gets straight to work.";
    },
    // Only while the sheet's button can take the tap (a free builder and the cost in hand): a disabled one would trap.
    when: (c) => { const h = firstWinHub(c); return h !== null && h.sheet === "upgrade" && !h.upgraded && h.builderSuggestion !== null; },
    until: (c) => c.signals.has("upgradeStarted") || hub(c)?.upgraded === true || hub(c)?.sheet !== "upgrade",
  },
  {
    // A walkthrough-priority tip, so it speaks before PLAY 2 (registration order breaks the tie).
    id: "hub.upgradeWatch", kind: "tip", pose: "thumbsUp", screen: "hub", priority: GuidePriority.Walkthrough, after: ["hub.upgradeGo"], topic: "builders",
    target: { dom: HubTargets.builderChip }, block: "none",
    text: "Already at it! The work goes on even while you're away.",
    when: (c) => { const h = firstWinHub(c); return h !== null && h.sheet === null && h.upgraded; },
  },
  {
    id: "hub.play2", kind: "do", pose: "pointing", screen: "hub", priority: GuidePriority.Walkthrough, after: ["hub.firstWin"],
    target: { dom: HubTargets.play }, block: "hard",
    text: (c) => `Ready for more? Tap **PLAY** for level ${hub(c)?.nextLevel ?? FIRST_WIN + 1}.`,
    when: (c) => { const h = firstWinHub(c); return h !== null && h.sheet === null && equipDone(h) && upgradeDone(h); },
    until: pressed,
  },
];

const hints: GuideStep[] = [
  {
    id: "hub.gearGate", kind: "info", pose: "pointing", screen: "hub", priority: GuidePriority.Safety, topic: "gear",
    target: { dom: HubTargets.gearTab },
    text: (c) => {
      const h = hub(c), n = h?.gearRequiredFrom ?? 0;
      return h && h.stashCount > 0
        ? `From level ${n}, raiders hit much harder, so **gear is a must**. Open the **Armoury** and put on your best items.`
        : `From level ${n}, raiders hit much harder, so **gear is a must**. Replay battles for chests full of it.`;
    },
    when: (c) => { const h = lobby(c); return h !== null && atGate(h); },
  },
  {
    id: "hub.gearGateEquip", kind: "do", pose: "pointing", screen: "hub", priority: GuidePriority.Safety, after: ["hub.gearGate"], topic: "gear",
    target: { dom: HubTargets.equipBest }, block: "none",
    text: (c) => `Tap an item, then **Equip** it. Every piece worn gets you ready for level ${hub(c)?.gearRequiredFrom ?? 0}.`,
    when: (c) => { const h = hub(c); return h !== null && h.sheet === "gear" && atGate(h) && h.stashCount > 0; },
    until: (c) => { const h = hub(c); return c.signals.has("gearEquipped") || h === null || h.sheet !== "gear" || !h.gearRequired; },
  },
  ...NEW_TOWERS.map((t): GuideStep => ({
    id: `hub.newTower.${t.id}`, kind: "info", pose: "pointing", screen: "hub", priority: GuidePriority.NewThing, topic: t.topic ?? "towers",
    target: { dom: HubTargets.towersTab },
    // Met in battle already (levelHints "New tower: Fire!"): the hub announcing it as ready would be old news.
    unless: [`hint.newTower.${t.id}`],
    text: t.topic ? `The **${t.name}** has joined us! Have a look in **Towers**.` : `The **${t.name}** tower has joined us! Have a look in **Towers**.`,
    when: (c) => { const h = lobby(c); return h !== null && h.highestCleared >= HINTS_FROM_CLEARED && h.newTowerIds.includes(t.id); },
    until: (c) => hub(c)?.newTowerIds.includes(t.id) === false,
  })),
  {
    id: "hub.stars", kind: "info", pose: "pointing", screen: "hub", priority: GuidePriority.NewThing, topic: "stars",
    target: { dom: HubTargets.starRow },
    text: "Every level has **3 stars**. Here's what this one asks.",
    when: (c) => (lobby(c)?.highestCleared ?? 0) >= HINTS_FROM_CLEARED,
  },
  {
    id: "hub.orders", kind: "tip", pose: "pointing", screen: "hub", priority: GuidePriority.Tip, topic: "orders",
    target: { dom: HubTargets.eventsTab }, block: "none",
    text: "Daily **Orders** pay out chests. Find them in **Events** and finish all three each day.",
    when: (c) => { const h = lobby(c); return h !== null && h.ordersUnlocked && h.highestCleared >= HINTS_FROM_CLEARED; },
  },
  {
    id: "hub.guideIntro", kind: "tip", pose: "neutral", screen: "hub", priority: GuidePriority.Tip,
    target: { dom: HubTargets.menuButton }, block: "none",
    text: "Need a refresher? My tips live in the **⚙ menu → Guide**.",
    when: (c) => (lobby(c)?.highestCleared ?? 0) >= HINTS_FROM_CLEARED,
  },
];

export const hubScript: GuideScript = { id: "hub", steps: [...firstSession, ...hints, ...featureHubSteps] };
